import * as React from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';

export interface AuthStepperProps {
  /** Ordered step labels. */
  steps: string[];
  /** 0-based index of the active step. */
  current: number;
  className?: string;
}

export const AuthStepper: React.FC<AuthStepperProps> = ({ steps, current, className }) => {
  const { t } = useTranslation();
  return (
    <ol className={cn('flex items-start', className)} aria-label={t('auth.progress')}>
      {steps.map((label, i) => {
        const isComplete = i < current;
        const isActive = i === current;
        const isLast = i === steps.length - 1;
        // Each step is an equal-width column with the circle centered by two
        // half-connectors, so circles stay evenly spaced regardless of label
        // length. The two halves of a segment colour together once passed; the
        // outer halves (before the first / after the last circle) are hidden.
        const leftActive = i <= current; // segment entering this step
        const rightActive = i < current; // segment leaving this step
        return (
          <li key={label} className="flex flex-1 flex-col items-center gap-2">
            <div className="flex w-full items-center">
              <span
                className={cn(
                  'h-0.5 flex-1 rounded-full transition-colors',
                  i === 0 ? 'bg-transparent' : leftActive ? 'bg-success-200' : 'bg-border',
                )}
                aria-hidden
              />
              <span
                className={cn(
                  'mx-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors',
                  isComplete && 'border-success-600 bg-success-600 text-white',
                  isActive && 'border-2 border-foreground text-foreground',
                  !isComplete && !isActive && 'border-border text-muted-foreground',
                )}
                aria-current={isActive ? 'step' : undefined}
              >
                {isComplete ? <Check className="h-4 w-4" aria-hidden /> : i + 1}
              </span>
              <span
                className={cn(
                  'h-0.5 flex-1 rounded-full transition-colors',
                  isLast ? 'bg-transparent' : rightActive ? 'bg-success-200' : 'bg-border',
                )}
                aria-hidden
              />
            </div>
            <span
              className={cn(
                'hidden text-center text-[11px] font-medium leading-tight sm:block',
                isActive ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

AuthStepper.displayName = 'AuthStepper';
